/**
 * @file index.tsx
 *
 * @fileoverview Renders all terms sections for the job.
 */
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { JobDetailTermsSection } from '~/jobiqo/components/Job/Detail/components/TermsSection';
import { Job } from '~/jobiqo/types/types';
import JobAllTermsSlot from '~/jobiqo/components/Slots/JobAllTermsSlot';
import { getFormattedSalaryRangeOutput } from '~/jobiqo/components/Job/Detail/utils';
import { Text } from '~/vcl/atoms/13-text/03-text';
import {
  Expander,
  ExpanderContent,
  ExpanderTrigger,
} from '~/vcl/atoms/22-expander';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';

interface JobDetailShowAllTermsProps {
  /**
   * The job.
   */
  job: Job;
}

export const JobDetailShowAllTerms = ({ job }: JobDetailShowAllTermsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();

  return (
    <Expander key={job.id} isOpen={isOpen} onChange={() => setIsOpen(!isOpen)}>
      <ExpanderContent>
        {job.fieldsOfStudy && job.fieldsOfStudy.length > 0 && (
          <JobDetailTermsSection
            Title={
              <FormattedMessage
                id="job_detail_fields_of_study"
                defaultMessage="Fields of study"
              />
            }
            terms={job.fieldsOfStudy}
          />
        )}
        {job.degreeLevel && job.degreeLevel.length > 0 && (
          <JobDetailTermsSection
            Title={
              <FormattedMessage
                id="job_detail_degree_level"
                defaultMessage="Required degree level"
              />
            }
            terms={job.degreeLevel}
          />
        )}
        {job.yearsOfExperience && job.yearsOfExperience.length > 0 && (
          <JobDetailTermsSection
            Title={
              <FormattedMessage
                id="job_detail_years_of_experience"
                defaultMessage="Years of experience"
              />
            }
            terms={job.yearsOfExperience}
          />
        )}
        {job.salaryRange && job.salaryRange.length > 0 && (
          <JobDetailTermsSection
            Title={
              <FormattedMessage
                id="job_detail_salary_range"
                defaultMessage="Salary range"
              />
            }
            terms={job.salaryRange}
          />
        )}
        {(job['salaryRangeFree']?.minSalary ||
          job['salaryRangeFree']?.maxSalary) && (
          <>
            <Text marginBottom={4} fontWeight={600}>
              <FormattedMessage
                id="job_detail_salary_range"
                defaultMessage="Salary range"
              />
            </Text>
            <div className="jcl-list-styled">
              <ul>
                <li>
                  {getFormattedSalaryRangeOutput(job['salaryRangeFree'], intl)}
                </li>
              </ul>
            </div>
            <Spacing />
          </>
        )}
        {job.languages && job.languages.length > 0 && (
          <JobDetailTermsSection
            Title={
              <FormattedMessage
                id="job_detail_languages"
                defaultMessage="Required languages"
              />
            }
            terms={job.languages}
          />
        )}
        {job.skills && job.skills.length > 0 && (
          <JobDetailTermsSection
            Title={
              <FormattedMessage
                id="job_detail_skills"
                defaultMessage="Required skills"
              />
            }
            terms={job.skills}
          />
        )}
        <JobAllTermsSlot job={job} />
      </ExpanderContent>
      <Spacing />
      <ExpanderTrigger>
        <Text tag="span" color="primary">
          {isOpen ? (
            <FormattedMessage
              id="job_details_show_all_terms_close"
              defaultMessage="Show less"
            />
          ) : (
            <FormattedMessage
              id="job_details_show_all_terms_open"
              defaultMessage="Show more"
            />
          )}
        </Text>
      </ExpanderTrigger>
    </Expander>
  );
};

export default JobDetailShowAllTerms;
